import { useMemo } from "react";
import { MapContainer, TileLayer, CircleMarker, Tooltip as LeafletTooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";

// Country centroids for positioning markers
const COUNTRY_COORDS: Record<string, [number, number]> = {
  "Russia": [61.5, 105],
  "United States": [39.8, -98.5],
  "China": [35.8, 104.1],
  "Ukraine": [48.3, 31.1],
  "Iran": [32.4, 53.6],
  "Israel": [31.0, 34.8],
  "North Korea": [40.3, 127.5],
  "India": [20.5, 78.9],
  "Turkey": [38.9, 35.2],
  "France": [46.2, 2.2],
  "UK": [55.3, -3.4],
  "South Korea": [35.9, 127.7],
  "Pakistan": [30.3, 69.3],
  "Japan": [36.2, 138.2],
  "Saudi Arabia": [23.8, 45.0],
};

interface CountryArmsBasic {
  country: string;
  flag: string;
  readiness: number;
  trend: "building" | "stable" | "depleting";
  totalInventory: number;
}

interface ArmsWorldMapProps {
  data: CountryArmsBasic[];
  selectedCountry: string;
  onSelectCountry: (country: string) => void;
}

const getColor = (readiness: number) => {
  if (readiness > 80) return "hsl(120, 70%, 45%)";
  if (readiness > 50) return "hsl(45, 90%, 50%)";
  if (readiness > 25) return "hsl(0, 80%, 50%)";
  return "hsl(0, 90%, 40%)";
};

const ArmsWorldMap = ({ data, selectedCountry, onSelectCountry }: ArmsWorldMapProps) => {
  const maxInventory = useMemo(() => Math.max(...data.map((d) => d.totalInventory)), [data]);

  return (
    <div className="h-[360px] rounded-lg overflow-hidden border border-border">
      <MapContainer
        center={[25, 30]}
        zoom={2}
        minZoom={2}
        maxZoom={5}
        scrollWheelZoom={true}
        className="h-full w-full"
        style={{ background: "hsl(var(--background))" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.esri.com/">Esri</a>'
          url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}"
        />

        {data.map((d) => {
          const coords = COUNTRY_COORDS[d.country];
          if (!coords) return null;
          const radius = 8 + (d.totalInventory / maxInventory) * 28;
          const isSelected = d.country === selectedCountry;
          return (
            <CircleMarker
              key={d.country}
              center={coords}
              radius={radius}
              pathOptions={{
                color: isSelected ? "hsl(var(--primary))" : getColor(d.readiness),
                fillColor: getColor(d.readiness),
                fillOpacity: isSelected ? 0.7 : 0.4,
                weight: isSelected ? 3 : 1.5,
              }}
              eventHandlers={{ click: () => onSelectCountry(d.country) }}
            >
              <LeafletTooltip direction="top" className="!bg-card !border-border !text-foreground !text-xs !rounded-md !shadow-lg">
                <div className="font-semibold">{d.flag} {d.country}</div>
                <div className="text-[10px] text-muted-foreground">
                  Total inventory: {d.totalInventory.toLocaleString()} | Readiness: {d.readiness}%
                </div>
                <div className="text-[10px]">
                  Trend: <span className={d.trend === "building" ? "text-green-400" : d.trend === "depleting" ? "text-red-400" : "text-yellow-400"}>
                    {d.trend}
                  </span>
                </div>
              </LeafletTooltip>
            </CircleMarker>
          );
        })}
      </MapContainer>
    </div>
  );
};

export default ArmsWorldMap;
